import React, { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useQuery, useQueries, useMutation, useQueryClient } from 'react-query';
import toast from 'react-hot-toast';
import { crmFormsApi } from '../services/api';
import { DEFAULT_SCHEMA, STATUS_LABELS, apiErrorMessage, pct, publicFormUrl, slugify } from '../utils/forms';

const copyText = async (text, okMsg) => {
  try {
    await navigator.clipboard.writeText(text);
    toast.success(okMsg);
  } catch {
    toast.error('Não foi possível copiar');
  }
};

function NewFormModal({ source, onClose, onCreated }) {
  const [name, setName] = useState(source ? `${source.name} (cópia)` : '');
  const [slug, setSlug] = useState(source ? `${source.slug}-copia` : '');
  const [slugTouched, setSlugTouched] = useState(!!source);
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const res = await crmFormsApi.create({
        name: name.trim(),
        slug,
        status: 'draft',
        schema: source?.schema || DEFAULT_SCHEMA,
        chatwootAccountId: null,
      });
      toast.success('Formulário criado');
      onCreated(res.data.form);
    } catch (err) {
      toast.error(err.message || 'Erro ao criar formulário');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[60] bg-black/40 flex items-center justify-center p-4" onClick={onClose}>
      <form onSubmit={submit} onClick={(e) => e.stopPropagation()} className="card p-6 w-full max-w-md space-y-4">
        <h2 className="text-lg font-bold font-headline">{source ? 'Duplicar formulário' : 'Novo formulário'}</h2>
        <div>
          <label className="block text-xs font-bold text-secondary uppercase tracking-wider mb-1">Nome</label>
          <input
            className="input" required maxLength={120} value={name} autoFocus
            onChange={(e) => {
              setName(e.target.value);
              if (!slugTouched) setSlug(slugify(e.target.value));
            }}
          />
        </div>
        <div>
          <label className="block text-xs font-bold text-secondary uppercase tracking-wider mb-1">Slug (link público)</label>
          <input
            className="input" required pattern="[a-z0-9-]+" value={slug}
            onChange={(e) => { setSlugTouched(true); setSlug(e.target.value.toLowerCase()); }}
          />
          <p className="text-xs text-slate-500 mt-1 break-all">{publicFormUrl(slug || '...')}</p>
        </div>
        <div className="flex justify-end gap-2">
          <button type="button" className="btn btn-secondary" onClick={onClose}>Cancelar</button>
          <button type="submit" className="btn btn-primary" disabled={saving || !name.trim() || !slug}>
            {saving ? 'Criando...' : 'Criar'}
          </button>
        </div>
      </form>
    </div>
  );
}

const Metric = ({ label, value }) => (
  <div>
    <p className="lg:hidden text-[10px] uppercase tracking-wider text-slate-400">{label}</p>
    <p className="font-bold">{value}</p>
  </div>
);

const IconBtn = ({ icon, title, onClick, danger }) => (
  <button
    type="button" title={title} aria-label={title} onClick={onClick}
    className={`p-2 rounded-lg transition-colors ${danger ? 'text-red-500 hover:bg-red-50' : 'text-slate-500 hover:bg-primary/10 hover:text-primary'}`}
  >
    <span className="material-symbols-outlined text-[20px]">{icon}</span>
  </button>
);

export default function Forms() {
  const navigate = useNavigate();
  const base = useLocation().pathname.startsWith('/crm-view') ? '/crm-view/forms' : '/forms';
  const queryClient = useQueryClient();
  const [modal, setModal] = useState(null); // { source? }

  const { data, isLoading, error } = useQuery('crm-forms', () => crmFormsApi.list());
  const forms = data?.data?.forms || [];

  const statsQueries = useQueries(
    forms.map((f) => ({
      queryKey: ['crm-form-stats', f.id],
      queryFn: () => crmFormsApi.getStats(f.id),
      staleTime: 60 * 1000,
    }))
  );
  const statsById = {};
  forms.forEach((f, i) => { statsById[f.id] = statsQueries[i]?.data?.data?.stats; });

  const removeMutation = useMutation((id) => crmFormsApi.remove(id), {
    onSuccess: () => {
      toast.success('Formulário excluído');
      queryClient.invalidateQueries('crm-forms');
    },
    onError: (err) => toast.error(err.message || 'Erro ao excluir'),
  });

  const handleDelete = (f) => {
    if (!window.confirm(`Excluir "${f.name}"? As respostas também serão removidas. Esta ação não pode ser desfeita.`)) return;
    removeMutation.mutate(f.id);
  };

  return (
    <div className="space-y-8">
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4">
        <div>
          <h1 className="text-3xl font-extrabold font-headline text-on-surface">Formulários</h1>
          <p className="text-sm text-slate-500 mt-1">Formulários públicos com coleta de respostas, estatísticas e entrega por webhook.</p>
        </div>
        <button className="btn btn-primary flex items-center gap-2 self-start" onClick={() => setModal({})}>
          <span className="material-symbols-outlined text-[18px]">add</span>
          Novo formulário
        </button>
      </div>

      {isLoading && <p className="text-slate-500">Carregando...</p>}
      {error && <p className="text-red-600">{apiErrorMessage(error, 'Erro ao carregar formulários')}</p>}

      {!isLoading && !error && forms.length === 0 && (
        <div className="card p-10 text-center">
          <span className="material-symbols-outlined text-5xl text-slate-300">dynamic_form</span>
          <p className="mt-3 font-bold">Nenhum formulário ainda</p>
          <p className="text-sm text-slate-500">Crie o primeiro para começar a coletar respostas.</p>
        </div>
      )}

      {forms.length > 0 && (
        <div className="space-y-3">
          <div className="hidden lg:grid grid-cols-[2fr_1fr_repeat(4,minmax(0,0.7fr))_auto] gap-4 px-6 text-[10px] font-label uppercase tracking-widest text-on-surface-variant">
            <span>Formulário</span><span>Status</span><span>Visitas</span><span>Envios</span><span>Conversão</span><span>Abandono</span><span className="w-[168px]" />
          </div>
          {forms.map((f) => {
            const s = statsById[f.id];
            const st = STATUS_LABELS[f.status] || STATUS_LABELS.draft;
            const abandonRate = s && s.startedCount > 0 ? s.abandonedCount / s.startedCount : 0;
            return (
              <div key={f.id} className="card px-6 py-4 grid grid-cols-2 lg:grid-cols-[2fr_1fr_repeat(4,minmax(0,0.7fr))_auto] gap-4 items-center">
                <button className="text-left col-span-2 lg:col-span-1 min-w-0" onClick={() => navigate(`${base}/${f.id}`)}>
                  <p className="font-bold truncate hover:text-primary">{f.name}</p>
                  <p className="text-xs text-slate-500 truncate">/f/{f.slug}</p>
                </button>
                <div><span className={`text-xs font-bold px-2 py-1 rounded-full ${st.color}`}>{st.label}</span></div>
                <Metric label="Visitas" value={s ? s.totalVisits : '—'} />
                <Metric label="Envios" value={s ? s.completeCount : '—'} />
                <Metric label="Conversão" value={s ? pct(Math.min(s.conversionRate, 1)) : '—'} />
                <Metric label="Abandono" value={s ? pct(abandonRate) : '—'} />
                <div className="col-span-2 lg:col-span-1 flex items-center gap-1 lg:justify-end">
                  <IconBtn icon="edit" title="Editar" onClick={() => navigate(`${base}/${f.id}`)} />
                  <IconBtn icon="link" title="Copiar link público" onClick={() => copyText(publicFormUrl(f.slug), 'Link copiado')} />
                  <IconBtn icon="content_copy" title="Duplicar" onClick={() => setModal({ source: f })} />
                  <IconBtn icon="delete" title="Excluir" danger onClick={() => handleDelete(f)} />
                </div>
              </div>
            );
          })}
        </div>
      )}

      {modal && (
        <NewFormModal
          source={modal.source}
          onClose={() => setModal(null)}
          onCreated={(form) => {
            setModal(null);
            queryClient.invalidateQueries('crm-forms');
            navigate(`${base}/${form.id}`);
          }}
        />
      )}
    </div>
  );
}
